import {
  Image,
  PDFDownloadLink,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { Download } from "lucide-react";
import React from "react";
import { getFieldLabel } from "../../../helpers/labFieldMappings";
import { Button } from "../../ui/button";
import GenericPDFDocument from "../GenericPDFDocument";

const styles = StyleSheet.create({
  section: {
    marginBottom: 16,
    paddingBottom: 8,
    borderBottom: "1px solid #ccc",
    gap: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 8,
    backgroundColor: "#0052FD10",
    padding: 4,
  },
  item: {
    marginBottom: 4,
  },
  label: {
    fontSize: 10,
    color: "#888",
    fontWeight: 500,
  },
  value: {
    fontSize: 12,
    color: "#222",
    fontWeight: 600,
    marginBottom: 2,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    rowGap: 24,
  },
  gridItem: {
    width: "33.33%",
    paddingRight: 8,
    marginBottom: 0,
  },
  therapeuticItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  therapeuticLabel: {
    fontSize: 10,
    color: "#222",
    fontWeight: 600,
    width: "50%",
  },
  therapeuticValue: {
    fontSize: 10,
    color: "#222",
    fontWeight: 500,
  },
});

const LabItemPDF = ({ title, content }) => (
  <View style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <Text style={styles.label}>{title}</Text>
    <Text style={styles.value}>{content || "N/A"}</Text>
  </View>
);

const TestSectionPDF = ({ title, tests }) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>{title}</Text>
    <View style={styles.grid}>
      {tests.map((test, index) => (
        <View key={index} style={styles.gridItem}>
          <Text style={styles.value}>{getFieldLabel(test)}</Text>
        </View>
      ))}
    </View>
  </View>
);

const TherapeuticDrugMonitoringPDF = ({ labData }) => {
  const therapeuticFieldIds = [
    "digoxinLastDose",
    "lithiumLastDose",
    "phenobarbitalLastDose",
    "phenytoinLastDose",
    "primidoneLastDose",
    "valproicAcidLastDose",
    "cyclosporineLastDose",
    "vancomycinLastDose",
    "preDoseLastDose",
    "postDoseLastDose",
    "gentamicinLastDose",
    "tobramycinLastDose",
    "preDoseLevelLastDose",
    "extendedIntervalPediatricsLastDose",
    "postDoseLevelLastDose",
    "extendedIntervalLastDose",
    "hr22PostLevelNeonatesLastDose",
  ];

  const fieldsWithData = therapeuticFieldIds.filter(
    (fieldId) => labData?.[fieldId],
  );

  if (fieldsWithData.length === 0) {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Therapeutic Drug Monitoring</Text>
        <Text style={styles.label}>
          No therapeutic drug monitoring tests selected
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Therapeutic Drug Monitoring</Text>
      <Text style={styles.value}>Date & Time of last dose Required</Text>
      <View style={{ flexDirection: "column", gap: 20 }}>
        {fieldsWithData.map((fieldId, index) => {
          const dateValue = labData[fieldId];
          const date = dateValue ? new Date(dateValue) : null;
          return (
            <View key={index} style={styles.therapeuticItem}>
              <View style={{ width: "50%", flexDirection: "column" }}>
                <Text style={styles.therapeuticLabel}>
                  {getFieldLabel(fieldId)}
                </Text>
              </View>
              <View style={{ width: "50%", flexDirection: "row", gap: 20 }}>
                <View style={{ flexDirection: "column", gap: 4 }}>
                  <Text style={styles.therapeuticLabel}>Date</Text>
                  <Text style={styles.therapeuticValue}>
                    {date ? date.toLocaleDateString("en-US") : "N/A"}
                  </Text>
                </View>
                <View style={{ flexDirection: "column", gap: 4 }}>
                  <Text style={styles.therapeuticLabel}>Time</Text>
                  <Text style={styles.therapeuticValue}>
                    {date
                      ? date.toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : "N/A"}
                  </Text>
                </View>
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const BloodGasPDF = ({ labData }) => {
  const hasBloodGasData =
    labData?.o2Device ||
    labData?.o2Therapy ||
    labData?.temprature ||
    (labData?.bloodGases && labData.bloodGases.length > 0);

  if (!hasBloodGasData) {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Blood Gas</Text>
        <Text style={styles.label}>No blood gas information available</Text>
      </View>
    );
  }

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Blood Gas</Text>
      <View style={styles.grid}>
        <View style={styles.gridItem}>
          <LabItemPDF title="O2 Device" content={labData?.o2Device} />
        </View>
        <View style={styles.gridItem}>
          <LabItemPDF title="O2 Therapy" content={labData?.o2Therapy} />
        </View>
        <View style={styles.gridItem}>
          <LabItemPDF title="Temperature" content={labData?.temprature} />
        </View>
      </View>
      {labData?.bloodGases && labData.bloodGases.length > 0 && (
        <View style={{ marginTop: 8 }}>
          <Text style={styles.value}>Selected Blood Gas Tests:</Text>
          <View style={styles.grid}>
            {labData.bloodGases.map((test, index) => (
              <View key={index} style={styles.gridItem}>
                <Text style={styles.value}>{getFieldLabel(test)}</Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
};

const DownloadLabForm = ({ labData, appointmentId }) => {
  const generatePDFContent = () => {
    const sections = [];

    // Basic Information
    sections.push(
      <View key="basic" style={styles.section}>
        <Text style={styles.sectionTitle}>Basic Information</Text>
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 20 }}>
          {[
            {
              title: "Date",
              content: labData?.createdAt
                ? new Date(labData.createdAt).toLocaleDateString("en-US")
                : "N/A",
            },
            {
              title: "Time",
              content: labData?.createdAt
                ? new Date(labData.createdAt).toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                : "N/A",
            },
            {
              title: "Payment Responsibility",
              content: labData?.paymentResponsibility,
            },
            {
              title: "Fasting Required",
              content: labData?.fastingRequired ? "Yes" : "No",
            },
            {
              title: "Ordering Physician/NP Contact info",
              content: "03331234567",
            },
            { title: "Medical Record Number", content: labData?.id },
            { title: "Facility & Unit", content: "Doc Mobil" },
          ].map((item, idx) => (
            <View key={item.title + idx} style={{ width: "45%" }}>
              <LabItemPDF title={item.title} content={item.content} />
            </View>
          ))}
        </View>
      </View>,
    );

    // Chemistry
    if (labData?.chemistry1Tests && labData.chemistry1Tests.length > 0) {
      sections.push(
        <TestSectionPDF
          key="chemistry"
          title="Chemistry"
          tests={labData.chemistry1Tests}
        />,
      );
    } else {
      sections.push(
        <View key="chemistry" style={styles.section}>
          <Text style={styles.sectionTitle}>Chemistry</Text>
          <Text style={styles.label}>No chemistry tests selected</Text>
        </View>,
      );
    }

    // Hematology
    if (labData?.hematologyTests && labData.hematologyTests.length > 0) {
      sections.push(
        <TestSectionPDF
          key="hematology"
          title="Hematology"
          tests={labData.hematologyTests}
        />,
      );
    } else {
      sections.push(
        <View key="hematology" style={styles.section}>
          <Text style={styles.sectionTitle}>Hematology</Text>
          <Text style={styles.label}>No hematology tests selected</Text>
        </View>,
      );
    }

    // Coagulation
    if (labData?.coagulationTests && labData.coagulationTests.length > 0) {
      sections.push(
        <TestSectionPDF
          key="coagulation"
          title="Coagulation"
          tests={labData.coagulationTests}
        />,
      );
    } else {
      sections.push(
        <View key="coagulation" style={styles.section}>
          <Text style={styles.sectionTitle}>Coagulation</Text>
          <Text style={styles.label}>No coagulation tests selected</Text>
        </View>,
      );
    }

    // Immunology
    if (labData?.immunologyTests && labData.immunologyTests.length > 0) {
      sections.push(
        <TestSectionPDF
          key="immunology"
          title="Immunology"
          tests={labData.immunologyTests}
        />,
      );
    } else {
      sections.push(
        <View key="immunology" style={styles.section}>
          <Text style={styles.sectionTitle}>Immunology</Text>
          <Text style={styles.label}>No immunology tests selected</Text>
        </View>,
      );
    }

    // Cardiac Function and Lipids
    if (
      labData?.cardiacFunctionAndLipidTests &&
      labData.cardiacFunctionAndLipidTests.length > 0
    ) {
      sections.push(
        <TestSectionPDF
          key="cardiac"
          title="Cardiac Function and Lipids"
          tests={labData.cardiacFunctionAndLipidTests}
        />,
      );
    } else {
      sections.push(
        <View key="cardiac" style={styles.section}>
          <Text style={styles.sectionTitle}>Cardiac Function and Lipids</Text>
          <Text style={styles.label}>No cardiac tests selected</Text>
        </View>,
      );
    }

    // Tolerance Tests
    if (labData?.toleranceTests && labData.toleranceTests.length > 0) {
      sections.push(
        <TestSectionPDF
          key="tolerance"
          title="Tolerance Tests"
          tests={labData.toleranceTests}
        />,
      );
    } else {
      sections.push(
        <View key="tolerance" style={styles.section}>
          <Text style={styles.sectionTitle}>Tolerance Tests</Text>
          <Text style={styles.label}>No tolerance tests selected</Text>
        </View>,
      );
    }

    // Nutritional Status
    if (
      labData?.nutritionalStatusTests &&
      labData.nutritionalStatusTests.length > 0
    ) {
      sections.push(
        <TestSectionPDF
          key="nutritional"
          title="Nutritional Status"
          tests={labData.nutritionalStatusTests}
        />,
      );
    } else {
      sections.push(
        <View key="nutritional" style={styles.section}>
          <Text style={styles.sectionTitle}>Nutritional Status</Text>
          <Text style={styles.label}>No nutritional status tests selected</Text>
        </View>,
      );
    }

    // Endocrine and Tumor Markers
    if (
      labData?.endocrineAndTumorMarkers &&
      labData.endocrineAndTumorMarkers.length > 0
    ) {
      sections.push(
        <TestSectionPDF
          key="endocrine"
          title="Endocrine and Tumor Markers"
          tests={labData.endocrineAndTumorMarkers}
        />,
      );
    } else {
      sections.push(
        <View key="endocrine" style={styles.section}>
          <Text style={styles.sectionTitle}>Endocrine and Tumor Markers</Text>
          <Text style={styles.label}>
            No endocrine and tumor marker tests selected
          </Text>
        </View>,
      );
    }

    // Therapeutic Drug Monitoring
    sections.push(
      <TherapeuticDrugMonitoringPDF key="therapeutic" labData={labData} />,
    );

    // Serum Toxicology
    if (
      labData?.serumToxicologyTests &&
      labData.serumToxicologyTests.length > 0
    ) {
      sections.push(
        <TestSectionPDF
          key="toxicology"
          title="Serum Toxicology"
          tests={labData.serumToxicologyTests}
        />,
      );
    } else {
      sections.push(
        <View key="toxicology" style={styles.section}>
          <Text style={styles.sectionTitle}>Serum Toxicology</Text>
          <Text style={styles.label}>No serum toxicology tests selected</Text>
        </View>,
      );
    }

    // Blood Gas
    sections.push(<BloodGasPDF key="bloodgas" labData={labData} />);

    sections.push(
      <View
        key="signature-row"
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          marginTop: 20,
        }}
      >
        <View
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            width: "50%",
          }}
        >
          <Text
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: "#1E1E1E",
              marginBottom: "8px",
              textAlign: "left",
            }}
          >
            Signature
          </Text>
          {labData?.appointment?.doctor?.signature?.url ? (
            <View style={{ width: 150, marginLeft: -20 }}>
              <Image
                src={`${labData.appointment.doctor.signature.url}?ext=.png`}
                style={{ width: 150, objectFit: "contain" }}
              />
            </View>
          ) : (
            <Text style={{ fontSize: 12, color: "#888" }}>
              No signature available
            </Text>
          )}
        </View>
        <View>
          <Text style={{ fontSize: 14, fontWeight: 600 }}>Created Date</Text>
          <Text style={{ fontSize: 14, fontWeight: 600 }}>
            {labData?.createdAt
              ? new Date(labData.createdAt).toLocaleDateString("en-US")
              : "N/A"}
          </Text>
        </View>
      </View>,
    );

    return sections;
  };

  return (
    <PDFDownloadLink
      document={
        <GenericPDFDocument heading="Laboratory test request form">
          {generatePDFContent()}
        </GenericPDFDocument>
      }
      fileName={`lab_form_${appointmentId}.pdf`}
    >
      {({ loading: pdfLoading }) => (
        <Button variant="primary" disabled={pdfLoading}>
          <Download className="mr-2 h-4 w-4" />
          {pdfLoading ? "Preparing PDF..." : "Download"}
        </Button>
      )}
    </PDFDownloadLink>
  );
};

export default DownloadLabForm;
